import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faEnvelope } from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { uniq } from "lodash-es";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import {
  Deal,
  EntitiesSupportedByEmailFeature,
  EntitiesSupportingEmail,
  EntityTypesSupportedByEmailFeature,
} from "@mapmycustomers/shared/types/entity";
import useDebouncedValue from "@mapmycustomers/shared/util/hook/useDebouncedValue";
import { Button, useModal } from "@mapmycustomers/ui";

import { checkEmailSupporting, showEmailCreationModal } from "@app/store/email/actions";
import { fetchPeople } from "@app/store/person/actions";
import useAnalytics from "@app/util/contexts/useAnalytics";

import messages from "./messages";
import RecipientType from "./RecipientType";
import SelectEmailRecipientType from "./SelectEmailRecipientType";
import showRecipientConfirmationModal from "./util/showRecipientConfirmationModal";
import useEmailEntities from "./util/useEmailEntities";

const allRecipientTypes: RecipientType[] = [
  RecipientType.Company,
  RecipientType.Person,
  RecipientType.CompanyPerson,
];

interface EmailButtonProps {
  disabled?: boolean;
  isMapMode?: boolean;
  onCheckEmailSupporting: typeof checkEmailSupporting;
  onFetchPeople: typeof fetchPeople;
  onShowEmailCreationModal: typeof showEmailCreationModal;
  selectedEntities: Set<EntitiesSupportingEmail>;
}

const EmailButton: React.FC<EmailButtonProps> = ({
  disabled,
  isMapMode = false,
  onCheckEmailSupporting,
  onFetchPeople,
  onShowEmailCreationModal,
  selectedEntities,
}) => {
  const intl = useIntl();
  const [modal, contextHolder] = useModal();
  const analyticsIssuer = useAnalytics();

  const [loading, setLoading] = useState<boolean>(false);

  const selectedEntitiesDebounced = useDebouncedValue(selectedEntities);

  const selectedEntitiesDebouncedArray = useMemo(
    () => Array.from(selectedEntitiesDebounced),
    [selectedEntitiesDebounced]
  );

  const {
    allCompanies,
    companies,
    hasEntitiesFromDeal,
    people,
    recipientTypes,
    someEntitiesDontHaveEmail,
  } = useEmailEntities(selectedEntitiesDebouncedArray, setLoading);

  const entityTypes = useMemo(
    () => uniq(selectedEntitiesDebouncedArray.map(({ entity }) => entity)),
    [selectedEntitiesDebouncedArray]
  );

  const handleEmail = useCallback(
    (recipientType?: RecipientType) => {
      const onShowEmailCreationModalExtended = (
        emailRecipientType: EntityTypesSupportedByEmailFeature,
        emailRecipients: EntitiesSupportedByEmailFeature[]
      ) => {
        onShowEmailCreationModal({
          deals: selectedEntitiesDebouncedArray.filter(
            (entity): entity is Deal => entity.entity === EntityType.DEAL
          ),
          recipients: emailRecipients,
          recipientType: emailRecipientType,
        });
      };

      switch (recipientType ?? Array.from(recipientTypes)[0]) {
        case RecipientType.Company:
          onShowEmailCreationModalExtended(EntityType.COMPANY, companies);
          break;
        case RecipientType.CompanyPerson:
          {
            onFetchPeople({
              callback: (peopleList) => {
                onShowEmailCreationModalExtended(EntityType.PERSON, peopleList.data);
              },
              options: {
                // we are requesting only minimal set of column to decrease amount of downloading data
                $columns: ["id", "name", "email"],
                $filters: {
                  $and: [
                    {
                      "accounts.id": { $in: allCompanies.map(({ id }) => id) },
                    },
                  ],
                  includeAccessStatus: true,
                },
                $limit: 1000,
                $order: "name",
              },
            });
          }
          break;
        case RecipientType.Person:
          onShowEmailCreationModalExtended(EntityType.PERSON, people);
          break;
      }
    },
    [
      companies,
      allCompanies,
      people,
      recipientTypes,
      selectedEntitiesDebouncedArray,
      onFetchPeople,
      onShowEmailCreationModal,
    ]
  );

  const handleClick = useCallback(() => {
    analyticsIssuer.clicked(["Bulk Email Lassoed Pins"]);
    onCheckEmailSupporting({
      modal,
      successfulCallback: async () => {
        try {
          let recipientType: RecipientType | undefined = undefined;
          // we need to ask user to confirm companies people to avoid misunderstandings
          if (recipientTypes.size > 1 || recipientTypes.has(RecipientType.CompanyPerson)) {
            const displayRecipientTypes = allRecipientTypes.filter((recipientType) =>
              recipientTypes.has(recipientType)
            );
            await showRecipientConfirmationModal(modal, {
              cancelText: intl.formatMessage(messages.modalEntitySelectorCancelText),
              centered: true,
              content: (
                <SelectEmailRecipientType
                  displayRecipientTypes={displayRecipientTypes}
                  entityTypes={entityTypes}
                  hasEntitiesFromDeal={hasEntitiesFromDeal}
                  onChangeRecipientType={(type: RecipientType) => {
                    recipientType = type;
                  }}
                />
              ),
              icon: null,
              okText: intl.formatMessage(messages.modalEntitySelectorOkText),
              title: intl.formatMessage(messages.modalEntitySelectorTitle),
            });
          } else if (recipientTypes.size === 1 && hasEntitiesFromDeal) {
            await showRecipientConfirmationModal(modal, {
              cancelText: intl.formatMessage(messages.modalAssociationsSentCancel),
              centered: true,
              content: intl.formatMessage(messages.modalAssociationsSentText),
              okText: intl.formatMessage(messages.modalEntitySelectorOkText),
              title: intl.formatMessage(messages.modalAssociationsSentTitle),
            });
          } else if (someEntitiesDontHaveEmail) {
            await showRecipientConfirmationModal(modal, {
              cancelText: intl.formatMessage(messages.modalNotAllEntitiesHaveEmailCancel),
              centered: true,
              content: intl.formatMessage(messages.modalNotAllEntitiesHaveEmailText),
              okText: intl.formatMessage(messages.modalNotAllEntitiesHaveEmailOk),
              title: intl.formatMessage(messages.modalNotAllEntitiesHaveEmailTitle),
            });
          }
          handleEmail(recipientType);
        } catch {
          /* empty */
        }
      },
    });
  }, [
    analyticsIssuer,
    onCheckEmailSupporting,
    modal,
    recipientTypes,
    hasEntitiesFromDeal,
    someEntitiesDontHaveEmail,
    handleEmail,
    intl,
    entityTypes,
  ]);

  const buttonDisabled = (!loading && !recipientTypes.size) || disabled;

  return (
    <>
      <Tooltip
        placement="bottom"
        title={intl.formatMessage(
          isMapMode ? messages.buttonDisabledWarningForMapMode : messages.buttonDisabledWarning
        )}
        trigger={buttonDisabled ? ["hover"] : []}
      >
        <Button
          disabled={buttonDisabled}
          leftIcon={<FontAwesomeIcon icon={faEnvelope} />}
          loading={loading}
          onClick={handleClick}
          type="filled"
        >
          {intl.formatMessage(messages.title)}
        </Button>
      </Tooltip>
      {contextHolder}
    </>
  );
};

const mapDispatchToProps = {
  onCheckEmailSupporting: checkEmailSupporting,
  onFetchPeople: fetchPeople,
  onShowEmailCreationModal: showEmailCreationModal,
};

export default connect(null, mapDispatchToProps)(EmailButton);
